import { useEffect, useState } from "react";
import axios from "axios";
import { Eye, Mail, CalendarDays, Lock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Stat = ({ icon: Icon, label, value, testId }) => (
  <div data-testid={testId} className="border border-[#D8D4CA] bg-white p-8">
    <div className="flex items-center gap-2 text-[#4A525B]">
      <Icon size={14} />
      <p className="text-[11px] font-semibold uppercase tracking-[0.22em]">{label}</p>
    </div>
    <p className="mt-4 font-serif text-5xl text-[#122336]">{value}</p>
  </div>
);

export default function Dashboard() {
  const [token, setToken] = useState(() => sessionStorage.getItem("dash_token") || "");
  const [input, setInput] = useState("");
  const [authError, setAuthError] = useState("");
  const [loadError, setLoadError] = useState("");
  const [summary, setSummary] = useState(null);
  const [messages, setMessages] = useState([]);

  const load = async (tk) => {
    try {
      const headers = { "X-Dashboard-Token": tk };
      const [s, m] = await Promise.all([
        axios.get(`${API}/visits/summary`, { headers }),
        axios.get(`${API}/contact`, { headers }),
      ]);
      setSummary(s.data);
      setMessages(m.data);
      setAuthError("");
      setLoadError("");
    } catch (e) {
      if (e.response?.status === 401) {
        sessionStorage.removeItem("dash_token");
        setToken("");
        setAuthError("That access code is not correct.");
      } else {
        setLoadError("Could not refresh the data. Retrying automatically.");
      }
    }
  };

  useEffect(() => {
    if (!token) return;
    load(token);
    const t = setInterval(() => load(token), 30000);
    return () => clearInterval(t);
  }, [token]);

  const unlock = (e) => {
    e.preventDefault();
    sessionStorage.setItem("dash_token", input);
    setToken(input);
  };

  if (!token) {
    return (
      <div data-testid="dashboard-lock" className="flex min-h-screen items-center justify-center bg-[#F6F5F2] px-6">
        <form onSubmit={unlock} className="w-full max-w-sm border border-[#D8D4CA] bg-white p-10">
          <div className="flex items-center gap-2 text-[#D18237]">
            <Lock size={14} />
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em]">Private View</p>
          </div>
          <h1 className="mt-4 font-serif text-2xl text-[#0A0F16]">Enter your access code.</h1>
          <input
            data-testid="dashboard-token-input"
            type="password"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Access code"
            className="mt-6 w-full border border-[#D8D4CA] bg-white px-4 py-3 text-sm text-[#0A0F16] focus:outline-none focus:ring-2 focus:ring-[#122336]"
          />
          {authError && <p data-testid="dashboard-auth-error" className="mt-3 text-xs text-red-600">{authError}</p>}
          <button
            data-testid="dashboard-unlock-btn"
            type="submit"
            className="mt-5 w-full bg-[#122336] px-6 py-3 text-xs font-semibold uppercase tracking-[0.15em] text-[#F6F5F2] transition-colors duration-300 hover:bg-[#D18237]"
          >
            Unlock
          </button>
        </form>
      </div>
    );
  }

  const maxCount = Math.max(1, ...(summary?.last_7_days || []).map((d) => d.count));
  const fmt = (iso) => new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  const hostLabel = (h) => {
    if (!h || h === "unknown") return "earlier visits";
    return h.replace(/^www\./, "");
  };

  return (
    <div data-testid="dashboard-page" className="min-h-screen bg-[#F6F5F2] px-6 py-16 lg:px-10">
      <div className="mx-auto max-w-5xl">
        <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#D18237]">The Signal Room</p>
        <h1 className="mt-3 font-serif text-4xl tracking-tight text-[#0A0F16]">Who's been on the site.</h1>
        <p className="mt-3 text-sm text-[#4A525B]">Private view. Refreshes every 30 seconds.</p>
        {loadError && <p data-testid="dashboard-load-error" className="mt-3 text-xs text-red-600">{loadError}</p>}

        <div className="mt-12 grid gap-6 md:grid-cols-3">
          <Stat icon={Eye} label="Total Visits" value={summary?.total_visits ?? "…"} testId="stat-total-visits" />
          <Stat icon={CalendarDays} label="Visits Today" value={summary?.today ?? "…"} testId="stat-today-visits" />
          <Stat icon={Mail} label="Messages" value={messages.length} testId="stat-messages" />
        </div>

        <div className="mt-12 border border-[#D8D4CA] bg-white p-8">
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#4A525B]">Last 7 Days</p>
          <div data-testid="visits-chart" className="mt-8 flex h-40 items-end gap-3">
            {(summary?.last_7_days || []).map((d) => (
              <div key={d.date} className="flex flex-1 flex-col items-center gap-2">
                <span className="text-xs text-[#4A525B]">{d.count}</span>
                <div className="w-full bg-[#122336] transition-[height] duration-700" style={{ height: `${(d.count / maxCount) * 100}%`, minHeight: 2 }} />
                <span className="text-[10px] uppercase tracking-wider text-[#9AA6B2]">{d.date.slice(5)}</span>
              </div>
            ))}
          </div>
          <div data-testid="host-tally" className="mt-8 flex flex-wrap gap-3 border-t border-[#EFECE6] pt-6">
            {(summary?.by_host || []).map((h) => (
              <span key={h.host} className="inline-flex items-center gap-2 border border-[#D8D4CA] bg-[#F6F5F2] px-4 py-2 text-xs text-[#4A525B]">
                {hostLabel(h.host)}
                <span className="font-serif text-base text-[#122336]">{h.count}</span>
              </span>
            ))}
          </div>
        </div>

        <div className="mt-12 grid gap-6 lg:grid-cols-2">
          <div className="border border-[#D8D4CA] bg-white p-8">
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#4A525B]">Recent Visits</p>
            <div data-testid="recent-visits-list" className="mt-6 space-y-3">
              {(summary?.recent || []).length === 0 && <p className="text-sm text-[#9AA6B2]">No visits yet.</p>}
              {(summary?.recent || []).map((v) => (
                <div key={v.id} className="flex items-center justify-between border-b border-[#EFECE6] pb-3 text-sm">
                  <span className="text-[#0A0F16]">{fmt(v.created_at)}</span>
                  <span className="text-xs text-[#9AA6B2]">{hostLabel(v.host)}{v.referrer ? ` · via ${v.referrer}` : ""}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="border border-[#D8D4CA] bg-white p-8">
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#4A525B]">Messages</p>
            <div data-testid="messages-list" className="mt-6 space-y-6">
              {messages.length === 0 && <p className="text-sm text-[#9AA6B2]">No messages yet.</p>}
              {messages.map((m) => (
                <div key={m.id} className="border-b border-[#EFECE6] pb-5">
                  <div className="flex items-baseline justify-between">
                    <p className="font-serif text-lg text-[#122336]">{m.name}</p>
                    <span className="text-xs text-[#9AA6B2]">{fmt(m.created_at)}</span>
                  </div>
                  <p className="text-xs text-[#4A525B]">{m.email}{m.organization ? ` · ${m.organization}` : ""}</p>
                  <p className="mt-2 text-sm leading-relaxed text-[#4A525B]">{m.message}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
